import {
	Component,
	input,
	output,
	computed,
	forwardRef,
	booleanAttribute,
	effect,
	inject,
	viewChild,
	ChangeDetectorRef,
	ElementRef,
	ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	FloatLabelType,
	MatFormFieldModule,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

export interface InputChangeEvent {
	value: string;
	event: Event;
}

export interface InputFocusEvent {
	value: string;
	focused: boolean;
}

export type InputType =
	| 'text'
	| 'email'
	| 'password'
	| 'tel'
	| 'url'
	| 'search'
	| 'number';
export type InputAppearance = 'fill' | 'outline';

@Component({
	selector: 'jepto-ui-input',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatButtonModule,
	],
	templateUrl: './jepto-ui-input.component.html',
	styleUrl: './jepto-ui-input.component.scss',
	host: {
		class: 'jepto-ui-input',
	},
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => JeptoUiInputComponent),
			multi: true,
		},
	],
})
export class JeptoUiInputComponent implements ControlValueAccessor {
	// Input signals
	placeholder = input('');
	label = input('');
	hint = input('');
	errorMessage = input('');
	type = input<InputType>('text');
	appearance = input<InputAppearance>('outline');
	/**
	 * When `appearance` is `fill`, keep the underline fill style but remove the
	 * Material focus/hover background fill (see Application input / Figma).
	 */
	transparentFillBackground = input(false);
	/**
	 * Figma Application fill input (`39500:90886`) — compact floated label,
	 * underline, and surface background (`--application-fill-background`).
	 * Use without a prefix icon when the search glyph sits in a sibling column.
	 */
	applicationFill = input(false);
	/** Overrides `--application-fill-background` when `applicationFill` is true (e.g. `transparent`, `#fff`). */
	applicationFillBackground = input<string | undefined>(undefined);
	/** Overrides `--application-fill-underline-color` when `applicationFill` is true (hex/rgba). */
	applicationFillUnderlineColor = input<string | undefined>(undefined);
	/** Apply Figma dark application-fill tokens (`39500:91014`) — underline, label, text. */
	applicationFillDark = input(false);
	/** Material outline label position — use `always` when label + placeholder show together. */
	floatLabel = input<FloatLabelType>('auto');
	disabled = input(false, { transform: booleanAttribute });
	readonly = input(false, { transform: booleanAttribute });
	/** Semibold value text (13px / 600) for application-style fill inputs. */
	semiboldValue = input(false, { transform: booleanAttribute });
	required = input(false, { transform: booleanAttribute });
	maxLength = input<number | null>(null);
	minLength = input<number | null>(null);
	prefixIcon = input('');
	suffixIcon = input('');
	showClearButton = input(false);
	autocomplete = input('');
	/** Optional `name` for autofill isolation (distinct per field on the same page). */
	fieldName = input<string>('');

	readonly nativeInput =
		viewChild<ElementRef<HTMLInputElement>>('nativeInput');
	spellcheck = input(true);
	tabIndex = input(0);
	/** Optional stable id for the native input (e.g. external `<label for>`). */
	inputId = input<string | undefined>(undefined);
	/** Optional data-testid for QA. */
	testId = input<string>('');

	// Output signals
	valueChange = output<InputChangeEvent>();
	focusChange = output<InputFocusEvent>();
	enterPressed = output<string>();
	iconClick = output<'prefix' | 'suffix'>();
	clearClick = output<void>();

	// Internal state
	private _value = '';
	private _focused = false;
	private readonly _autoInputId = `jepto-input-${Math.random().toString(36).slice(2, 11)}`;
	private readonly cdr = inject(ChangeDetectorRef);

	// ControlValueAccessor methods
	private onChange: (value: string) => void = () => {};
	private onTouched: () => void = () => {};

	// Computed properties
	showError = computed(
		() => !!(this.errorMessage() && this.errorMessage().trim())
	);
	showHint = computed(
		() => !!(this.hint() && this.hint().trim() && !this.showError())
	);
	hasPrefix = computed(
		() => !!(this.prefixIcon() && this.prefixIcon().trim())
	);
	readonly useApplicationFill = computed(
		() => this.appearance() === 'fill' && this.applicationFill()
	);
	readonly resolvedApplicationFillBackground = computed(() => {
		if (!this.useApplicationFill()) {
			return null;
		}
		return this.applicationFillBackground() ?? 'transparent';
	});
	readonly useTransparentFillBackground = computed(
		() =>
			this.appearance() === 'fill' &&
			this.transparentFillBackground() &&
			!this.applicationFill()
	);
	hasSuffix = computed(
		() =>
			!!(this.suffixIcon() && this.suffixIcon().trim()) ||
			this.showClearButton()
	);
	showClear = computed(
		() =>
			this.showClearButton() &&
			this._value.length > 0 &&
			!this.disabled() &&
			!this.readonly()
	);
	readonly resolvedInputId = computed(() => {
		const explicit = this.inputId();
		if (explicit) {
			return explicit;
		}
		const name = this.fieldName().trim();
		if (name) {
			return name;
		}
		return this._autoInputId;
	});

	constructor() {
		effect(() => {
			this.fieldName();
			this.#syncFieldName();
		});
	}

	#syncFieldName(): void {
		const name = this.fieldName().trim();
		const el = this.nativeInput()?.nativeElement;
		if (!el) {
			return;
		}
		if (name) {
			el.setAttribute('name', name);
			el.name = name;
		} else {
			el.removeAttribute('name');
		}
	}

	get value(): string {
		return this._value;
	}

	get focused(): boolean {
		return this._focused;
	}

	// ControlValueAccessor implementation
	writeValue(value: string | number | null | undefined): void {
		this._value = value == null ? '' : String(value);
		// Forms call writeValue outside this view's own events; OnPush needs an explicit mark.
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		// Handled by input signal
	}

	// Event handlers
	onInputChange(event: Event): void {
		const target = event.target as HTMLInputElement;
		this._value = target.value;
		this.onChange(this._value);

		this.valueChange.emit({
			value: this._value,
			event,
		});
	}

	onInputFocus(): void {
		this.#syncFieldName();
		this._focused = true;
		this.focusChange.emit({
			value: this._value,
			focused: true,
		});
	}

	onInputBlur(): void {
		this._focused = false;
		this.onTouched();
		this.focusChange.emit({
			value: this._value,
			focused: false,
		});
	}

	onKeyPress(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			this.enterPressed.emit(this._value);
		}
	}

	onPrefixIconClick(): void {
		this.iconClick.emit('prefix');
	}

	onSuffixIconClick(): void {
		this.iconClick.emit('suffix');
	}

	onClearClick(): void {
		this._value = '';
		this.onChange(this._value);
		this.clearClick.emit();
		this.valueChange.emit({
			value: this._value,
			event: new Event('clear'),
		});
	}
}
